import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext.js';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import './Dashboard.css';
import ParentMeetingInvitesCard from '../meeting/ParentMeetingInvitesCard.js';
import MyAnnouncementsSection from '../announcements/MyAnnouncementsSection.js';

const PIE_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6'];

function StatCard({ title, value, subtext, variant = 'default' }) {
  return (
    <div className={`dashboard-stat-card dashboard-stat-card--${variant}`}>
      <div className="dashboard-stat-title">{title}</div>
      <div className="dashboard-stat-value">{value}</div>
      {subtext ? <div className="dashboard-stat-subtext">{subtext}</div> : null}
    </div>
  );
}

function DashboardPie({ title, data }) {
  return (
    <div className="dashboard-panel">
      <div className="dashboard-panel-header">
        <h3>{title}</h3>
      </div>
      <div className="chart-box">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={data || []}
              dataKey="value"
              nameKey="name"
              outerRadius={95}
              innerRadius={50}
              paddingAngle={3}
            >
              {(data || []).map((entry, index) => (
                <Cell key={`cell-${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ParentMessageCard({ unreadCount }) {
  const hasUnread = unreadCount > 0;

  return (
    <a href="/messages/inbox" className="parent-message-card-link">
      <div className={`parent-message-card ${hasUnread ? 'parent-message-card--highlight' : ''}`}>
        <div className="parent-message-card__top">
          <div className="parent-message-card__badge">Berichten</div>
          <div className="parent-message-card__icon">✉️</div>
        </div>

        <div className="parent-message-card__content">
          <h3>Nieuwe berichten</h3>
          <div className="parent-message-card__count">{unreadCount}</div>
          <p>
            {hasUnread
              ? 'Er staan ongelezen berichten voor je klaar.'
              : 'Je hebt momenteel geen nieuwe berichten.'}
          </p>
        </div>

        <div className="parent-message-card__footer">
          <span>Open inbox</span>
          <span className="parent-message-card__arrow">→</span>
        </div>
      </div>
    </a>
  );
}



export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { userInfo } = useAuth();
  const token = sessionStorage.getItem('jwtToken');
  const role = userInfo?.role;

  useEffect(() => {
    let mounted = true;

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/dashboard/overview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (mounted) {
          setStats(res.data);
        }
      } catch (error) {
        console.error('Dashboard ophalen mislukt:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (token) {
      fetchDashboard();
    }

    return () => {
      mounted = false;
    };
  }, [token]);

  const sortedLocations = useMemo(() => {
    return [...(stats?.locations || [])].sort((a, b) => b.students - a.students);
  }, [stats]);

  if (loading) {
    return <div className="dashboard-loading">Dashboard laden...</div>;
  }

  if (!stats) {
    return <div className="dashboard-loading">Geen dashboardgegevens beschikbaar.</div>;
  }

  if (role === 'ouder') {
    return (
      <div className="dashboard-page dashboard-page--parent">
        <div className="dashboard-parent-grid">
          <ParentMessageCard unreadCount={stats.cards?.unreadMessages || 0} />
          <ParentMeetingInvitesCard />
          <MyAnnouncementsSection  />

        </div>
      </div>
    );
  }

  if (role === 'docent') {
  return (
    <div className="dashboard-page dashboard-page--parent">
      <div className="dashboard-parent-grid">
        <a href="/messages/inbox" className="dashboard-stat-link">
          <StatCard
            title="Ongelezen berichten"
            value={stats.cards?.unreadMessages || 0}
            subtext="Ga naar inbox"
            variant="primary"
          />
        </a>

        <MyAnnouncementsSection />
      </div>
    </div>
  );
}

  return (
    <div className="dashboard-page">
      <div className="dashboard-topbar">
        <div>
          <h2 className="dashboard-title">Dashboard</h2>
          <p className="dashboard-subtitle">
            Overzicht van leerlingen, docenten, groepen, absenties en aandachtspunten
          </p>
        </div>
      </div>

      <div className="dashboard-stats-grid">
        <StatCard title="Leerlingen" value={stats.cards?.activeStudents || 0} variant="primary" />
        <StatCard title="Docenten" value={stats.cards?.activeTeachers || 0} />
        <StatCard title="Groepen" value={stats.cards?.activeGroups || 0} />
        <StatCard title="Locaties" value={stats.cards?.activeLocations || 0} />
        <StatCard title="Open absenties" value={stats.cards?.pendingAbsences || 0} variant="danger" />
        <a href="/messages/inbox" className="dashboard-stat-link">
          <StatCard title="Ongelezen berichten" value={stats.cards?.unreadMessages || 0} variant="success" />
        </a>
      </div>

      <div className="dashboard-main-grid">
        <div className="dashboard-panel dashboard-panel--wide">
          <div className="dashboard-panel-header">
            <h3>Leerlingen per schooljaar</h3>
          </div>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={stats.trends?.studentsPerSchoolYear || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="school_year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total_students" name="Totaal" stroke="#4f46e5" strokeWidth={3} />
                <Line type="monotone" dataKey="active_students" name="Actief" stroke="#10b981" strokeWidth={3} />
                <Line type="monotone" dataKey="exited_students" name="Uitgeschreven" stroke="#ef4444" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <h3>Absenties per maand</h3>
          </div>
          <div className="chart-box">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={stats.trends?.absencesPerMonth || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" name="Absenties" fill="#4f46e5" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <DashboardPie
          title="Absenties per reden"
          data={stats.distribution?.absenceByReason || []}
        />

        <DashboardPie
          title="Absenties per status"
          data={stats.distribution?.absenceByStatus || []}
        />
      </div>

      <div className="dashboard-bottom-grid">
        <div className="dashboard-panel dashboard-panel--wide">
          <div className="dashboard-panel-header">
            <h3>Locatievergelijking</h3>
          </div>

          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Locatie</th>
                  <th>Leerlingen</th>
                  <th>Docenten</th>
                  <th>Groepen</th>
                  <th>Open absenties</th>
                </tr>
              </thead>
              <tbody>
                {sortedLocations.length ? (
                  sortedLocations.map((location) => (
                    <tr key={location.location_id}>
                      <td>{location.location_name}</td>
                      <td>{location.students}</td>
                      <td>{location.teachers}</td>
                      <td>{location.group_count}</td>
                      <td>{location.pending_absences}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">Geen locaties gevonden</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <h3>Aandacht vereist</h3>
          </div>

          <div className="dashboard-alert-list">
            <div className="dashboard-alert-card">
              <div className="dashboard-alert-top">
                <span className="dashboard-alert-label">Groepen zonder docent</span>
                <strong className="dashboard-alert-count">
                  {stats.alerts?.groupsWithoutTeacher || 0}
                </strong>
              </div>

              {stats.alerts?.groupsWithoutTeacherList?.length > 0 && (
                <div className="dashboard-alert-details">
                  <ul className="dashboard-alert-items">
                    {stats.alerts.groupsWithoutTeacherList.map((group) => (
                      <li key={group.groep_id}>
                        <Link
                          to={`/editgroup/${group.groep_id}`}
                          className="dashboard-alert-link"
                        >
                          {group.groep_name}
                        </Link>
                        {group.location_name && (
                          <span className="dashboard-alert-meta">
                            {' '}— {group.location_name}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>

                  {(stats.alerts.groupsWithoutTeacher || 0) >
                    (stats.alerts.groupsWithoutTeacherList?.length || 0) && (
                      <small className="dashboard-alert-more">
                        en nog{' '}
                        {(stats.alerts.groupsWithoutTeacher || 0) -
                          (stats.alerts.groupsWithoutTeacherList?.length || 0)}{' '}
                        meer...
                      </small>
                    )}
                </div>
              )}
            </div>

            <div className="dashboard-alert-card">
              <div className="dashboard-alert-top">
                <span className="dashboard-alert-label">Locaties zonder groepen</span>
                <strong className="dashboard-alert-count">
                  {stats.alerts?.locationsWithoutGroups || 0}
                </strong>
              </div>

              {stats.alerts?.locationsWithoutGroupsList?.length > 0 && (
                <div className="dashboard-alert-details">
                  <ul className="dashboard-alert-items">
                    {stats.alerts.locationsWithoutGroupsList.map((location) => (
                      <li key={location.location_id}>
                        <Link
                          to={`/editlocatie/${location.location_id}`}
                          className="dashboard-alert-link"
                        >
                          {location.location_name}
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {(stats.alerts.locationsWithoutGroups || 0) >
                    (stats.alerts.locationsWithoutGroupsList?.length || 0) && (
                      <small className="dashboard-alert-more">
                        en nog{' '}
                        {(stats.alerts.locationsWithoutGroups || 0) -
                          (stats.alerts.locationsWithoutGroupsList?.length || 0)}{' '}
                        meer...
                      </small>
                    )}
                </div>
              )}
            </div>

            <div className="dashboard-alert-card">
              <div className="dashboard-alert-top">
                <span className="dashboard-alert-label">Leerlingen zonder ouderkoppeling</span>
                <strong className="dashboard-alert-count">
                  {stats.alerts?.studentsWithoutParents || 0}
                </strong>
              </div>

              {stats.alerts?.studentsWithoutParentsList?.length > 0 && (
                <div className="dashboard-alert-details">
                  <ul className="dashboard-alert-items">
                    {stats.alerts.studentsWithoutParentsList.map((student) => (
                      <li key={student.student_id}>
                        <Link
                          to={`/viewstudent/${student.student_id}`}
                          className="dashboard-alert-link"
                        >
                          {student.firstname} {student.lastname}
                        </Link>
                        {student.location_name ? (
                          <span className="dashboard-alert-meta"> — {student.location_name}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>

                  {(stats.alerts.studentsWithoutParents || 0) >
                    (stats.alerts.studentsWithoutParentsList?.length || 0) && (
                      <small className="dashboard-alert-more">
                        en nog{' '}
                        {(stats.alerts.studentsWithoutParents || 0) -
                          (stats.alerts.studentsWithoutParentsList?.length || 0)}{' '}
                        meer...
                      </small>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}